/**
 * Integrations Dashboard
 * 
 * Displays all data source integrations with status, health, and actions.
 * Follows the pattern from JobList and SourceList for consistency.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Badge, LoadingSpinner, EmptyState } from '@tracekit/core';
import { useWorkspace, getSAMApiKey, getProject, getPolicyPack } from '@tracekit/core';
import {
  dataSourceRegistry,
  DataSourceType,
  DataSourceRegistryEntry,
  HealthCheckResult,
} from '../services/dataSourceRegistry';
import { DataSourceConfig } from '../services/dataSourceIntegrations';
import { fetchFromDataSource } from '../services/dataSourceRegistry';
import { registerAPIIntegrationSource, listSources, triggerSync, updateSourceMetadata } from '@tracekit/core';
import { loadDataSourceConfigsFromPolicyPack } from '../utils/policyPackConfigLoader';
import { uploadArtifact } from '@tracekit/core';
import { submitIngestJob } from '@tracekit/core';
import {
  Database,
  RefreshCw,
  Play,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Search,
  Filter,
  Globe,
  Key,
  Zap,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  MapPin,
  FileText,
  DollarSign,
  Newspaper,
  Scale,
  Award,
  Rss,
  BookOpen,
  Radio,
  Building2,
  Upload,
} from 'lucide-react';
import { getZoneManager, Zone, ZONE_DEFINITIONS } from '../services/zoneManager';
import { RSS_FEED_REGISTRY, RSSFeedSource, CATEGORY_NAMES } from '../services/rssFeedRegistry';
import { SyncSettingsModal } from '../components/SyncSettingsModal';
import { CredentialManagementModal } from '../components/CredentialManagementModal';
import { CSVUploadModal } from '../components/CSVUploadModal';
import { SyncStatusBadge, SyncStatus } from '../components/SyncStatusBadge';
import { GeographicFilter, GeographicConfig } from '../components/GeographicFilter';
import { ServiceLineFilter, ServiceLine } from '../components/ServiceLineFilter';
import { GeographicFilter as GeoFilter } from '../utils/geographicFiltering';
import { getCredentialFields, requiresCredentials } from '../utils/credentialFields';

interface IntegrationStatus {
  source: DataSourceType;
  enabled: boolean;
  health: HealthCheckResult | null;
  lastSync: string | null;
  opportunitiesSynced: number;
  config: DataSourceConfig;
}

// Data source geographic mapping
const DATA_SOURCE_COUNTRIES: Record<DataSourceType, string[]> = {
  canada_grants: ['Canada'],
  canada_tenders: ['Canada'],
  samgov: ['United States'],
  worldbank: ['International'],
  usa_awards: ['United States'],
  usa_budgets: ['United States'],
  federal_register: ['United States'],
  executive_orders: ['United States'],
  rss_feeds: ['International', 'United States', 'United Kingdom'], // Multi-country
  bc_bid: ['Canada'],
  bids_tender: ['Canada'],
  bonfire: ['Canada', 'United States'],
  merx: ['Canada'],
  sap_ariba: ['International'],
  jaggaer: ['International'],
  city_portals: ['Canada'],
};

// Data source state/local mapping (for extensibility)
const DATA_SOURCE_STATES: Record<DataSourceType, string[]> = {
  canada_grants: [],
  canada_tenders: [],
  samgov: [], // Could be extended with state-specific sources
  worldbank: [],
  usa_awards: [],
  usa_budgets: [],
  federal_register: [],
  executive_orders: [],
  rss_feeds: [],
  bc_bid: ['British Columbia'],
  bids_tender: [],
  bonfire: [],
  merx: [],
  sap_ariba: [],
  jaggaer: [],
  city_portals: ['British Columbia', 'Ontario'],
};

// Helper function to get all unique countries
function getAllCountries(): string[] {
  const countries = new Set<string>();
  Object.values(DATA_SOURCE_COUNTRIES).forEach(countryList => {
    countryList.forEach(country => countries.add(country));
  });
  return Array.from(countries).sort();
}

// Helper function to get all unique states (for future extensibility)
function getAllStates(): string[] {
  const states = new Set<string>();
  Object.values(DATA_SOURCE_STATES).forEach(stateList => {
    stateList.forEach(state => states.add(state));
  });
  return Array.from(states).sort();
}

// Helper function to find a source by integration type (handles both meta and metadata fields)
function findSourceByIntegrationType(
  sources: any[],
  integrationType: string
): any | undefined {
  return sources.find(s => {
    if (s.type !== 'api_integration') return false;
    
    // Check both meta and metadata fields (API might return either)
    const meta = s.meta || s.metadata || {};
    const sourceIntegrationType = meta.integration_type || s.name;
    
    // Match by integration_type or name
    return sourceIntegrationType === integrationType || s.name === integrationType;
  });
}

// Helper function to ensure a source exists, creating it if needed
async function ensureSourceExists(
  projectId: string,
  source: DataSourceType,
  config: DataSourceConfig
): Promise<string> {
  try {
    const sources = await listSources(projectId);
    let integrationSource = findSourceByIntegrationType(sources, source);
    
    if (!integrationSource) {
      console.log(`[Ensure Source] Creating missing source for ${source}...`);
      const sourceName = source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
          // Build API config from the source config
          const apiConfig: Record<string, any> = {};
          if (source === 'samgov' && config.api_key) {
            apiConfig.api_key = config.api_key;
            console.log(`[Ensure Source] Setting SAM.gov API key in api_config (length: ${config.api_key.length})`);
          }
      if (source === 'rss_feeds') {
        apiConfig.feed_urls = RSS_FEED_REGISTRY.map(feed => feed.url);
      }
      
      // Build filters from config
      const filters: Record<string, any> = {};
      if (config.filters?.keywords) filters.keywords = config.filters.keywords;
      if (config.filters?.startDate) filters.posted_from = config.filters.startDate;
      if (config.filters?.limit) filters.limit = config.filters.limit;
      
      integrationSource = await registerAPIIntegrationSource({
        project_id: projectId,
        name: source,
        description: `${sourceName} API Integration`,
        integration_type: source as any,
        api_config: apiConfig,
        filters: filters,
        sync_frequency: config.sync_frequency || 'daily',
        enabled: config.enabled ?? true,
      });
      
      console.log(`[Ensure Source] Created source ${integrationSource.id} for ${source}`);
    }
    
    // Return the source ID (handle different possible field names)
    const sourceId = integrationSource.id || (integrationSource as any).source_id || (integrationSource as any).sourceId;
    if (!sourceId) {
      throw new Error(`Source ID not found for ${source}`);
    }
    return String(sourceId);
  } catch (error: any) {
    console.error(`[Ensure Source] Failed to ensure source exists for ${source}:`, error);
    throw error;
  }
}

// Helper function to get metrics (prioritizes dynamic metrics over static)
function getMetrics(
  integration: IntegrationStatus,
  sourceMetrics: Record<string, any>
): {
  responseTime: string;
  lastSync: string;
  itemCount: number;
  status: string;
  statusColor: string;
} {
  const dynamicMetrics = sourceMetrics[integration.source];
  
  return {
    responseTime: dynamicMetrics?.responseTime 
      ? `${dynamicMetrics.responseTime}ms`
      : integration.health?.response_time_ms 
      ? `${integration.health.response_time_ms}ms`
      : 'N/A',
    lastSync: dynamicMetrics?.lastSync 
      ? new Date(dynamicMetrics.lastSync).toLocaleString()
      : integration.lastSync || 'Never',
    itemCount: dynamicMetrics?.itemCount ?? integration.opportunitiesSynced,
    status: dynamicMetrics?.status && dynamicMetrics.status !== 'unknown'
      ? dynamicMetrics.status 
      : integration.enabled 
      ? (integration.health?.status && integration.health.status !== 'unknown' 
          ? integration.health.status 
          : (integration.lastSync || integration.opportunitiesSynced > 0 
              ? 'healthy' 
              : 'Unknown'))
      : 'Disabled',
    statusColor: dynamicMetrics?.status === 'healthy' || integration.health?.healthy
      ? 'text-green-600'
      : dynamicMetrics?.status === 'error' || integration.health?.status === 'error'
      ? 'text-red-600'
      : 'text-yellow-600',
  };
}

export interface IntegrationsDashboardProps {
  geographicConfig?: GeographicConfig;
  serviceLines?: ServiceLine[];
}

export function IntegrationsDashboard({ 
  geographicConfig,
  serviceLines,
}: IntegrationsDashboardProps = {}) {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'unhealthy' | 'disabled'>('all');
  const [expandedSource, setExpandedSource] = useState<DataSourceType | null>(null);
  const [previewData, setPreviewData] = useState<Record<string, { opportunities: any[]; awards: any[]; error?: string }>>({});
  const zoneManager = getZoneManager();
  const [selectedZone, setSelectedZone] = useState<Zone>(zoneManager.getCurrentZone());
  const [showRSSFeedDetails, setShowRSSFeedDetails] = useState(false);
  const [rssTestResults, setRssTestResults] = useState<Record<string, { status: 'testing' | 'success' | 'error'; message?: string }>>({});
  
  // Dynamic metrics tracking (updated on preview/sync)
  const [sourceMetrics, setSourceMetrics] = useState<Record<string, {
    responseTime: number;
    lastSync: string;
    itemCount: number;
    status: 'healthy' | 'degraded' | 'error';
  }>>({});
  
  // Country and State/Local filtering
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Geographic and Service Line filtering
  const [geographicFilter, setGeographicFilter] = useState<GeoFilter>({});
  const [selectedServiceLines, setSelectedServiceLines] = useState<string[]>([]);
  
  // Sync Settings Modal state
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncModalSource, setSyncModalSource] = useState<{
    source: DataSourceType;
    sourceId: string;
    sourceName: string;
    integrationType: string;
    config: DataSourceConfig;
  } | null>(null);
  
  // Credential Management Modal state
  const [credentialModalOpen, setCredentialModalOpen] = useState(false);
  const [credentialModalSource, setCredentialModalSource] = useState<{
    source: DataSourceType;
    sourceId: string;
    sourceName: string;
  } | null>(null);
  
  // CSV Upload Modal state
  const [csvUploadModalOpen, setCSVUploadModalOpen] = useState(false);
  
  // Update zone when zone manager changes
  React.useEffect(() => {
    const listener = (newZone: Zone) => {
      setSelectedZone(newZone);
    };
    zoneManager.addListener(listener);
    return () => zoneManager.removeListener(listener);
  }, []);
  
  // Get all registered data sources
  const allSources = dataSourceRegistry.getAll();
  
  // Load persisted sources from database (must be defined before useEffects that use it)
  const { data: persistedSources = [], isLoading: loadingSources } = useQuery({
    queryKey: ['integration-sources', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const sources = await listSources(projectId);
      // Filter to only API integration sources
      return sources.filter(s => s.type === 'api_integration');
    },
    enabled: !!projectId,
  });
  
  // Initialize sourceMetrics from persisted sources
  React.useEffect(() => {
    if (persistedSources && persistedSources.length > 0 && Object.keys(sourceMetrics).length === 0) {
      const initialMetrics: Record<string, {
        responseTime: number;
        lastSync: string;
        itemCount: number;
        status: 'healthy' | 'degraded' | 'error';
      }> = {};
      
      persistedSources.forEach(source => {
        // Handle both meta and metadata fields
        const meta = source.meta || source.metadata || {};
        const integrationType = meta.integration_type || source.name;
        const healthMetrics = meta.health_metrics || {};
        const syncMetrics = meta.sync_metrics || {};
        
        if (integrationType) {
          // Determine status from available data
          let status: 'healthy' | 'degraded' | 'error' = 'unknown' as any;
          
          if (healthMetrics.status) {
            // Use explicit health status
            status = healthMetrics.status === 'healthy' ? 'healthy' 
                   : healthMetrics.status === 'error' ? 'error'
                   : healthMetrics.status === 'degraded' ? 'degraded'
                   : 'unknown' as any;
          } else {
            // Infer from sync data
            const hasSyncData = source.last_sync_at || syncMetrics.last_sync_at;
            const hasItems = (syncMetrics.item_count || 0) > 0;
            const isEnabled = meta.enabled !== false;
            
            if (isEnabled && (hasSyncData || hasItems)) {
              status = 'healthy';
            } else if (isEnabled) {
              status = 'unknown' as any; // Enabled but no data yet
            } else {
              status = 'error' as any; // Disabled
            }
          }
          
          initialMetrics[integrationType] = {
            responseTime: healthMetrics.response_time_ms || 0,
            lastSync: source.last_sync_at || syncMetrics.last_sync_at || new Date().toISOString(),
            itemCount: syncMetrics.item_count || 0,
            status: status,
          };
        }
      });
      
      if (Object.keys(initialMetrics).length > 0) {
        setSourceMetrics(initialMetrics);
        console.log('[IntegrationsDashboard] Loaded persisted metrics from database:', initialMetrics);
      }
    }
  }, [persistedSources, sourceMetrics]);
  
  // Load project to get policy pack ID
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
  });

  // Load policy pack if project has one
  const { data: policyPack } = useQuery({
    queryKey: ['policy-pack', project?.policy_pack_id],
    queryFn: () => getPolicyPack(project!.policy_pack_id!),
    enabled: !!project?.policy_pack_id,
  });

  // Load stored configurations from policy pack, persisted sources, and defaults
  const { data: configurations, isLoading: loadingConfigs } = useQuery({
    queryKey: ['integration-configs', projectId, project?.policy_pack_id],
    queryFn: async () => {
      // First, try to load from policy pack
      let policyPackConfigs: Record<DataSourceType, DataSourceConfig> | null = null;
      if (project?.policy_pack_id) {
        try {
          policyPackConfigs = await loadDataSourceConfigsFromPolicyPack(
            project.policy_pack_id,
            projectId!
          );
          if (policyPackConfigs) {
            console.log('[IntegrationsDashboard] Loaded configs from policy pack:', Object.keys(policyPackConfigs).length);
          }
        } catch (error) {
          console.warn('[IntegrationsDashboard] Failed to load configs from policy pack:', error);
        }
      }

      // Load persisted sources to extract configurations
      const sources = await listSources(projectId!);
      const apiIntegrationSources = sources.filter(s => s.type === 'api_integration');
      
      // Build configs from persisted sources
      const persistedConfigs: Record<DataSourceType, DataSourceConfig> = {} as any;
      
      // Default configurations (fallback if source not found and policy pack doesn't have it)
      const defaultConfigs: Record<DataSourceType, DataSourceConfig> = {
        canada_grants: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental', 'assessment'] },
        },
        canada_tenders: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'] },
        },
        samgov: {
          enabled: (() => {
            const apiKey = getSAMApiKey();
            return !!apiKey;
          })(),
          api_key: getSAMApiKey(),
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'] },
        },
        worldbank: {
          enabled: true,
          sync_frequency: 'weekly',
          filters: { keywords: ['environmental'] },
        },
        usa_awards: {
          enabled: false,
          sync_frequency: 'weekly',
          filters: { keywords: ['environmental', 'technology'] },
        },
        usa_budgets: {
          enabled: false,
          sync_frequency: 'weekly',
          filters: {},
        },
        federal_register: {
          enabled: false,
          sync_frequency: 'weekly',
          filters: { keywords: ['environmental', 'procurement'] },
        },
        executive_orders: {
          enabled: false,
          sync_frequency: 'weekly',
          filters: { keywords: ['procurement', 'technology'] },
        },
        rss_feeds: {
          enabled: true,
          sync_frequency: 'hourly',
          filters: { keywords: ['government', 'procurement'] },
        },
        bc_bid: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'], provinces: ['BC'] },
        },
        bids_tender: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'], provinces: ['BC', 'ON'] },
        },
        bonfire: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'] },
        },
        merx: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'] },
        },
        sap_ariba: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'] },
        },
        jaggaer: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'] },
        },
        city_portals: {
          enabled: true,
          sync_frequency: 'daily',
          filters: { keywords: ['environmental'], provinces: ['BC', 'ON'] },
        },
      };
      
      // Extract configs from persisted sources
      for (const source of apiIntegrationSources) {
        const meta = source.meta || source.metadata || {};
        const integrationType = meta.integration_type || source.name;
        
        if (integrationType && allSources.find(s => s.metadata.id === integrationType)) {
          const apiConfig = meta.api_config || {};
          const filters = meta.filters || {};
          const syncFrequency = meta.sync_frequency || 'daily';
          const enabled = meta.enabled !== false; // Default to true if not explicitly false
          
          persistedConfigs[integrationType as DataSourceType] = {
            enabled,
            sync_frequency: syncFrequency as any,
            filters,
            api_key: apiConfig.api_key,
            api_url: apiConfig.api_url,
          };
        }
      }
      
      // Merge: policy pack configs → persisted configs → defaults
      // Priority: persisted configs (from database) > policy pack configs > hardcoded defaults
      // Start with hardcoded defaults as base
      const mergedConfigs: Record<DataSourceType, DataSourceConfig> = { ...defaultConfigs };
      
      // Override with policy pack configs (if available)
      if (policyPackConfigs) {
        for (const [key, value] of Object.entries(policyPackConfigs)) {
          mergedConfigs[key as DataSourceType] = value;
        }
      }
      
      // Finally, override with persisted configs (database takes highest precedence)
      for (const [key, value] of Object.entries(persistedConfigs)) {
        mergedConfigs[key as DataSourceType] = value;
      }
      
      // Special handling for SAM.gov - always check API key at runtime
      if (mergedConfigs.samgov) {
        const runtimeApiKey = getSAMApiKey();
        mergedConfigs.samgov.enabled = !!runtimeApiKey;
        mergedConfigs.samgov.api_key = runtimeApiKey;
      }
      
      console.log('[IntegrationsDashboard] Loaded configurations:', {
        fromPolicyPack: policyPackConfigs ? Object.keys(policyPackConfigs).length : 0,
        fromPersisted: Object.keys(persistedConfigs).length,
        fromDefaults: Object.keys(defaultConfigs).length,
        total: Object.keys(mergedConfigs).length,
      });
      
      return mergedConfigs;
    },
    enabled: !!projectId,
  });
  
  // Health check for all sources - only run on manual refresh, not on page load
  const [shouldCheckHealth, setShouldCheckHealth] = useState(false);
  const { data: healthChecks, refetch: refetchHealth } = useQuery({
    queryKey: ['integration-health', projectId],
    queryFn: async () => {
      if (!configurations) return {};
      
      const checks: Record<DataSourceType, HealthCheckResult> = {} as any;
      
      for (const [source, config] of Object.entries(configurations)) {
        if (config.enabled) {
          try {
            // Ensure config has all required fields
            const fullConfig: DataSourceConfig = {
              enabled: config.enabled,
              sync_frequency: config.sync_frequency || 'daily',
              filters: config.filters || {},
              api_key: config.api_key,
              api_url: config.api_url,
            };
            const healthResult = await dataSourceRegistry.test(
              source as DataSourceType,
              fullConfig
            );
            checks[source as DataSourceType] = healthResult;
            
            // Persist health check result to database
            try {
              // Ensure source exists, then update
              const sourceId = await ensureSourceExists(projectId!, source as DataSourceType, config);
              await updateSourceMetadata(sourceId, {
                health_status: healthResult.status || (healthResult.healthy ? 'healthy' : 'error'),
                health_message: healthResult.message || '',
                response_time_ms: healthResult.response_time_ms || 0,
                enabled: true,
              });
              console.log(`[Health Check] Saved result to database for ${source}`);
            } catch (saveError: any) {
              console.error(`[Health Check] Failed to save result to database for ${source}:`, saveError);
            }
          } catch (error: any) {
            console.error(`Health check failed for ${source}:`, error);
            const errorResult = {
              healthy: false,
              status: 'error',
              message: error.message || 'Health check failed',
              response_time_ms: 0,
              last_checked: new Date().toISOString(),
            };
            checks[source as DataSourceType] = errorResult;
            
            // Persist error state to database
            try {
              // Ensure source exists, then update
              const sourceId = await ensureSourceExists(projectId!, source as DataSourceType, config);
              await updateSourceMetadata(sourceId, {
                health_status: 'error',
                health_message: error.message || 'Health check failed',
                response_time_ms: 0,
                enabled: true,
              });
              console.log(`[Health Check] Saved error state to database for ${source}`);
            } catch (saveError: any) {
              console.error(`[Health Check] Failed to save error state to database for ${source}:`, saveError);
            }
          }
        } else {
          // For disabled sources, mark as disabled
          const disabledResult = {
            healthy: false,
            status: 'disabled',
            message: 'Data source is disabled',
            response_time_ms: 0,
            last_checked: new Date().toISOString(),
          };
          checks[source as DataSourceType] = disabledResult;
          
          // Persist disabled state to database
          try {
            // Ensure source exists, then update
            const sourceId = await ensureSourceExists(projectId!, source as DataSourceType, config);
            await updateSourceMetadata(sourceId, {
              health_status: 'disabled',
              health_message: 'Data source is disabled',
              enabled: false,
            });
          } catch (saveError: any) {
            console.error(`[Health Check] Failed to save disabled state to database for ${source}:`, saveError);
          }
        }
      }
      
      return checks;
    },
    enabled: !!configurations && shouldCheckHealth, // Only run when explicitly requested
    refetchInterval: false, // Don't auto-refresh
  });
  
  // Preview mutation (dry-run fetch) with dynamic metrics tracking
  const previewMutation = useMutation({
    mutationFn: async ({ source, config }: { source: DataSourceType; config: DataSourceConfig }) => {
      const startTime = Date.now();
      try {
        const result = await fetchFromDataSource(source, config, projectId!);
        const responseTime = Date.now() - startTime;
        return { 
          source, 
          opportunities: Array.isArray(result.opportunities) ? result.opportunities.slice(0, 5) : [], // Preview first 5
          awards: Array.isArray(result.awards) ? result.awards.slice(0, 5) : [], // Preview first 5 awards
          responseTime,
          totalCount: (result.opportunities?.length || 0) + (result.awards?.length || 0),
        };
      } catch (error: any) {
        const responseTime = Date.now() - startTime;
        console.error(`[Preview] Error fetching preview for ${source}:`, error);
        throw { ...error, responseTime, source };
      }
    },
    onSuccess: async ({ source, opportunities, awards, responseTime, totalCount }) => {
      setPreviewData(prev => ({ ...prev, [source]: { opportunities, awards } }));
      setExpandedSource(source);
      
      // Update dynamic metrics
      const metrics = {
        responseTime,
        lastSync: new Date().toISOString(),
        itemCount: opportunities.length + awards.length,
        status: 'healthy' as const,
      };
      
      setSourceMetrics(prev => ({
        ...prev,
        [source]: metrics
      }));
      
      // Persist to database
      try {
        // Ensure source exists, then update
        const sourceId = await ensureSourceExists(projectId!, source, variables.config);
        await updateSourceMetadata(sourceId, {
          health_status: 'healthy',
          health_message: `Preview successful: ${totalCount} items found`,
          response_time_ms: responseTime,
          last_sync_at: metrics.lastSync,
          item_count: metrics.itemCount,
          enabled: true,
        });
        console.log(`[Preview] Saved state to database for ${source}`);
        // Invalidate to refresh
        queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      } catch (error: any) {
        console.error(`[Preview] Failed to save state to database for ${source}:`, error);
        // Don't fail the preview if saving fails
      }
    },
    onError: async (error: any, variables) => {
      console.error(`[Preview] Failed to preview ${variables.source}:`, error);
      const responseTime = error.responseTime || 0;
      const source = variables.source;
      
      // Set error state in preview data
      setPreviewData(prev => ({ 
        ...prev, 
        [source]: { 
          opportunities: [], 
          awards: [],
          error: error.message || 'Failed to fetch preview data'
        } 
      }));
      
      // Update metrics with error status
      const metrics = {
        responseTime,
        lastSync: new Date().toISOString(),
        itemCount: 0,
        status: 'error' as const,
      };
      
      setSourceMetrics(prev => ({
        ...prev,
        [source]: metrics
      }));
      
      // Persist error state to database
      try {
        // Ensure source exists, then update
        const sourceId = await ensureSourceExists(projectId!, source, variables.config);
        await updateSourceMetadata(sourceId, {
          health_status: 'error',
          health_message: error.message || 'Failed to fetch preview data',
          response_time_ms: responseTime,
          last_sync_at: metrics.lastSync,
          item_count: 0,
          enabled: true,
        });
        console.log(`[Preview] Saved error state to database for ${source}`);
        // Invalidate to refresh
        queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      } catch (saveError: any) {
        console.error(`[Preview] Failed to save error state to database for ${source}:`, saveError);
      }
    },
  });
  
  // Sync mutation - registers API integration source and triggers backend sync worker
  const syncMutation = useMutation({
    mutationFn: async ({ source, config, filters }: { source: DataSourceType; config: DataSourceConfig; filters?: Record<string, any> }) => {
      const startTime = Date.now();
      const sourceName = source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const saveAsDefault = filters?.saveAsDefault || false;
      
      // Remove saveAsDefault from the actual filters to send to the API
      const actualFilters = { ...filters };
      delete actualFilters.saveAsDefault;
      
      try {
        console.log(`[Sync] Starting sync for ${source}...`);
        
        // Step 1: Check if API integration source exists, if not register it
        console.log(`[Sync] Checking if API integration source exists...`);
        const sources = await listSources(projectId!);
        let integrationSource = findSourceByIntegrationType(sources, source);
        
        if (!integrationSource) {
          console.log(`[Sync] Registering new API integration source for ${source}...`);
          
          // Build API config from the source config
          const apiConfig: Record<string, any> = {};
          if (source === 'samgov' && config.api_key) {
            apiConfig.api_key = config.api_key;
            console.log(`[Sync] Setting SAM.gov API key in api_config (length: ${config.api_key.length})`);
          }
          if (source === 'rss_feeds') {
            apiConfig.feed_urls = RSS_FEED_REGISTRY.map(feed => feed.url);
          }
          
          // Build filters from config and merge with modal filters
          const filters: Record<string, any> = {};
          if (config.keywords) filters.keywords = config.keywords;
          if (config.startDate) filters.posted_from = config.startDate;
          if (config.limit) filters.limit = config.limit;
          
          // Merge with filters from modal (modal filters take precedence)
          Object.assign(filters, actualFilters);
          
          integrationSource = await registerAPIIntegrationSource({
            project_id: projectId!,
            name: source,
            description: `${sourceName} API Integration`,
            integration_type: source as any,
            api_config: apiConfig,
            filters: filters,
            sync_frequency: 'daily',
            enabled: true,
          });
          console.log(`[Sync] API integration source registered: ${integrationSource.id}`);
        } else {
          console.log(`[Sync] Using existing API integration source: ${integrationSource.id}`);
          
          // If saveAsDefault is true, update the existing source with new filters
          if (saveAsDefault && Object.keys(actualFilters).length > 0) {
            console.log(`[Sync] Updating source with new default filters...`);
            // Note: In production, we'd call an update endpoint here
            // For now, the filters are applied to this sync only
          }
        }
        
        // Step 2: Trigger sync via backend worker
        console.log(`[Sync] Triggering backend sync worker...`);
        const syncResult = await triggerSync(integrationSource.id);
        console.log(`[Sync] Sync job created: ${syncResult.job_id}`);
        
        const responseTime = Date.now() - startTime;
        return { 
          source, 
          jobId: syncResult.job_id,
          responseTime,
          sourceId: integrationSource.id,
          tablesQueued: syncResult.tables_queued,
        };
      } catch (error: any) {
        const responseTime = Date.now() - startTime;
        console.error(`[Sync] Sync failed for ${source}:`, error);
        throw { ...error, responseTime, source };
      }
    },
    onSuccess: async ({ source, jobId, responseTime, sourceId }) => {
      const sourceName = source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const message = `✅ Sync Job Queued Successfully!\n\n` +
        `Source: ${sourceName}\n` +
        `Job ID: ${jobId}\n` +
        `Source ID: ${sourceId}\n\n` +
        `The backend worker will now:\n` +
        `• Fetch data from ${sourceName} API\n` +
        `• Create artifacts with fetched data\n` +
        `• Parse content and extract entities\n` +
        `• Create vector embeddings\n` +
        `• Build BM25 keyword index\n\n` +
        `Check the Jobs page to monitor progress.`;
      
      alert(message);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['artifacts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      
      // Update dynamic metrics
      const metrics = {
        responseTime,
        lastSync: new Date().toISOString(),
        itemCount: 0, // Will be updated when job completes
        status: 'healthy' as const,
      };
      
      setSourceMetrics(prev => ({
        ...prev,
        [source]: metrics
      }));
      
      // Persist to database
      try {
        await updateSourceMetadata(sourceId, {
          health_status: 'healthy',
          health_message: `Sync job queued successfully (Job ID: ${jobId})`,
          response_time_ms: responseTime,
          last_sync_at: metrics.lastSync,
          item_count: 0,
          enabled: true,
        });
        console.log(`[Sync] Saved state to database for ${source}`);
      } catch (error: any) {
        console.error(`[Sync] Failed to save state to database for ${source}:`, error);
        // Don't fail the sync if saving fails
      }
    },
    onError: async (error: any, variables) => {
      const { source, config } = variables;
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`❌ Sync Failed for ${source}\n\n${errorMessage}\n\nCheck console for details.`);
      const responseTime = error.responseTime || 0;
      
      // Update metrics with error status
      const metrics = {
        responseTime,
        lastSync: new Date().toISOString(),
        itemCount: 0,
        status: 'error' as const,
      };
      
      setSourceMetrics(prev => ({
        ...prev,
        [source]: metrics
      }));
      
      // Persist error state to database
      try {
        // Ensure source exists, then update
        const sourceId = await ensureSourceExists(projectId!, source, config);
        await updateSourceMetadata(sourceId, {
          health_status: 'error',
          health_message: errorMessage,
          response_time_ms: responseTime,
          last_sync_at: metrics.lastSync,
          item_count: 0,
          enabled: true,
        });
        console.log(`[Sync] Saved error state to database for ${source}`);
        // Invalidate to refresh
        queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      } catch (saveError: any) {
        console.error(`[Sync] Failed to save error state to database for ${source}:`, saveError);
      }
    },
  });
  
  // Build integration status list with persisted state from database
  const integrations: IntegrationStatus[] = allSources.map(entry => {
    const userConfig = configurations?.[entry.metadata.id];
    const defaultConfig = dataSourceRegistry.getDefaultConfig(entry.metadata.id) || {};
    
    // Find persisted source from database
    const persistedSource = findSourceByIntegrationType(persistedSources || [], entry.metadata.id);
    
    // Get persisted health metrics and sync metrics (handle both meta and metadata)
    const meta = persistedSource ? (persistedSource.meta || persistedSource.metadata || {}) : {};
    const persistedHealth = meta.health_metrics;
    const persistedSync = meta.sync_metrics;
    const persistedEnabled = meta.enabled;
    
    // Debug logging
    if (import.meta.env.DEV && persistedSource) {
      console.log(`[Status Load] ${entry.metadata.id}:`, {
        hasHealth: !!persistedHealth,
        healthStatus: persistedHealth?.status,
        hasSync: !!persistedSync,
        itemCount: persistedSync?.item_count,
        lastSync: persistedSource.last_sync_at || persistedSync?.last_sync_at,
      });
    }
    
    // Special handling for SAM.gov - check API key at runtime
    let enabled = persistedEnabled ?? userConfig?.enabled ?? defaultConfig?.enabled ?? false;
    let apiKey = userConfig?.api_key || defaultConfig?.api_key;
    
    if (entry.metadata.id === 'samgov') {
      // Always check API key at runtime for SAM.gov
      const runtimeApiKey = getSAMApiKey();
      if (runtimeApiKey) {
        enabled = true;
        apiKey = runtimeApiKey;
      } else {
        enabled = false;
      }
    }
    
    const config: DataSourceConfig = {
      enabled,
      sync_frequency: userConfig?.sync_frequency || defaultConfig?.sync_frequency || 'daily',
      filters: userConfig?.filters || defaultConfig?.filters || {},
      api_key: apiKey,
      api_url: userConfig?.api_url || defaultConfig?.api_url,
    };
    
    // Build health check result from persisted data
    let health: HealthCheckResult | null = null;
    if (persistedHealth) {
      // Only create health object if we have actual health check data
      // Don't create one with 'unknown' status as that's not a real health check
      const healthStatus = persistedHealth.status;
      if (healthStatus && healthStatus !== 'unknown') {
        health = {
          healthy: healthStatus === 'healthy',
          status: healthStatus,
          message: persistedHealth.message || '',
          response_time_ms: persistedHealth.response_time_ms || 0,
          last_checked: persistedHealth.last_checked || new Date().toISOString(),
        };
      }
    } else if (healthChecks?.[entry.metadata.id]) {
      health = healthChecks[entry.metadata.id];
    } else {
      // Infer status from available data if no explicit health check
      // If we have sync data, assume it's working
      const hasSyncData = persistedSource?.last_sync_at || persistedSync?.last_sync_at;
      const hasItems = (persistedSync?.item_count || 0) > 0;
      
      if (enabled && (hasSyncData || hasItems)) {
        // If enabled and has sync data, assume healthy
        health = {
          healthy: true,
          status: 'healthy',
          message: hasItems ? `Last synced: ${persistedSync?.item_count || 0} items` : 'Source is active',
          response_time_ms: persistedHealth?.response_time_ms || 0,
          last_checked: persistedSource?.last_sync_at || persistedSync?.last_sync_at || new Date().toISOString(),
        };
      } else if (enabled && !persistedSource) {
        // If enabled but no persisted source exists yet, show as ready to sync
        health = {
          healthy: true,
          status: 'ok',
          message: 'Ready for first sync - Click Preview or Sync Now to test',
          response_time_ms: 0,
          last_checked: new Date().toISOString(),
        };
      }
      // If enabled but no sync data yet, leave health as null
      // This way we won't show "Health Check Failed" for sources that just haven't been synced yet
    }
    
    return {
      source: entry.metadata.id,
      enabled: config.enabled,
      health: health,
      lastSync: persistedSource?.last_sync_at || persistedSync?.last_sync_at || null,
      opportunitiesSynced: persistedSync?.item_count || 0,
      config: config as DataSourceConfig,
    };
  });
  
  // Filter integrations with country and state support
  const filteredIntegrations = integrations.filter(integration => {
    const entry = allSources.find(s => s.metadata.id === integration.source);
    if (!entry) return false;
    
    // Search filter
    const matchesSearch =
      entry.metadata.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.metadata.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'active' && integration.enabled && integration.health?.healthy) ||
      (filterStatus === 'unhealthy' && integration.enabled && !integration.health?.healthy) ||
      (filterStatus === 'disabled' && !integration.enabled);
    
    // Country filter
    const matchesCountry = selectedCountries.length === 0 || 
      DATA_SOURCE_COUNTRIES[integration.source]?.some(country => selectedCountries.includes(country));
    
    // State filter (for future extensibility)
    const matchesState = selectedStates.length === 0 || 
      DATA_SOURCE_STATES[integration.source]?.some(state => selectedStates.includes(state)) ||
      DATA_SOURCE_STATES[integration.source]?.length === 0; // Include sources without state data if states are selected
    
    return matchesSearch && matchesFilter && matchesCountry && matchesState;
  });
  
  // Stats
  const activeCount = integrations.filter(i => i.enabled && i.health?.healthy).length;
  const unhealthyCount = integrations.filter(i => i.enabled && !i.health?.healthy).length;
  const disabledCount = integrations.filter(i => !i.enabled).length;
  
  // Sync Settings Modal handlers
  const handleOpenSyncModal = async (integration: IntegrationStatus) => {
      try {
        // Find the registered source ID
        const sources = await listSources(projectId!);
        let integrationSource = findSourceByIntegrationType(sources, integration.source);
      
      // If source doesn't exist, register it first (required for preview)
      if (!integrationSource) {
        console.log(`[Sync Modal] Registering new API integration source for ${integration.source}...`);
        
        const sourceName = integration.source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        
        // Build API config from the source config
        const apiConfig: Record<string, any> = {};
        if (integration.source === 'samgov' && integration.config.api_key) {
          apiConfig.api_key = integration.config.api_key;
          console.log(`[Sync Modal] Setting SAM.gov API key in api_config (length: ${integration.config.api_key.length})`);
        }
        if (integration.source === 'rss_feeds') {
          apiConfig.feed_urls = RSS_FEED_REGISTRY.map(feed => feed.url);
        }
        
        // Build filters from config
        const filters: Record<string, any> = {};
        if (integration.config.keywords) filters.keywords = integration.config.keywords;
        if (integration.config.startDate) filters.posted_from = integration.config.startDate;
        if (integration.config.limit) filters.limit = integration.config.limit;
        
        integrationSource = await registerAPIIntegrationSource({
          project_id: projectId!,
          name: integration.source,
          description: `${sourceName} API Integration`,
          integration_type: integration.source as any,
          api_config: apiConfig,
          filters: filters,
          sync_frequency: 'daily',
          enabled: true,
        });
        
        console.log(`[Sync Modal] API integration source registered: ${integrationSource.id}`);
      }
      
      const sourceName = integration.source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      setSyncModalSource({
        source: integration.source,
        sourceId: integrationSource.id, // Now guaranteed to exist
        sourceName: sourceName,
        integrationType: integration.source,
        config: integration.config,
      });
      setSyncModalOpen(true);
    } catch (error: any) {
      console.error(`[Sync Modal] Failed to open sync modal:`, error);
      // Show error to user (you might want to add a toast/alert here)
      alert(`Failed to open sync settings: ${error.message || 'Unknown error'}`);
    }
  };
  
  const handleConfirmSync = (filters: Record<string, any>) => {
    if (syncModalSource) {
      syncMutation.mutate({
        source: syncModalSource.source,
        config: syncModalSource.config,
        filters,
      });
      setSyncModalOpen(false);
      setSyncModalSource(null);
    }
  };
  
  // Credential Management handlers
  const handleOpenCredentialModal = async (integration: IntegrationStatus) => {
    try {
      const sources = await listSources(projectId!);
      let integrationSource = findSourceByIntegrationType(sources, integration.source);
      
      if (!integrationSource) {
        // Ensure source exists first
        const sourceId = await ensureSourceExists(projectId!, integration.source, integration.config);
        integrationSource = { id: sourceId };
      }
      
      const sourceName = integration.source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      setCredentialModalSource({
        source: integration.source,
        sourceId: integrationSource.id || (integrationSource as any).source_id || (integrationSource as any).sourceId,
        sourceName: sourceName,
      });
      setCredentialModalOpen(true);
    } catch (error: any) {
      console.error(`[Credential Modal] Failed to open:`, error);
      alert(`Failed to open credential management: ${error.message || 'Unknown error'}`);
    }
  };
  
  const handleSaveCredentials = async (credentials: Record<string, string>) => {
    if (!credentialModalSource || !projectId) return;
    
    try {
      // Update source metadata with credentials
      await updateSourceMetadata(projectId, credentialModalSource.sourceId, {
        credentials: credentials,
        // Mark credentials as updated
        credentials_updated_at: new Date().toISOString(),
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      queryClient.invalidateQueries({ queryKey: ['integration-configs', projectId] });
      
      console.log(`[Credential Management] Saved credentials for ${credentialModalSource.sourceName}`);
    } catch (error: any) {
      console.error('[Credential Management] Failed to save credentials:', error);
      throw error;
    }
  };
  
  // Get existing credentials from source metadata
  const getExistingCredentials = (): Record<string, string> => {
    if (!credentialModalSource || !persistedSources) return {};
    
    const source = findSourceByIntegrationType(persistedSources, credentialModalSource.source);
    if (!source) return {};
    
    const meta = source.meta || source.metadata || {};
    return meta.credentials || {};
  };
  
  // Handle CSV upload
  const handleCSVUpload = async (data: any[], metadata: { source: string; template: string }) => {
    try {
      console.log(`[CSV Upload] Uploading ${data.length} opportunities from ${metadata.template}`);
      
      // Create a JSON artifact with the normalized data
      const artifactContent = JSON.stringify({
        opportunities: data,
        metadata: {
          source: metadata.source,
          template: metadata.template,
          uploadedAt: new Date().toISOString(),
          count: data.length,
        },
      }, null, 2);
      
      const blob = new Blob([artifactContent], { type: 'application/json' });
      const file = new File([blob], `csv_upload_${metadata.source}_${Date.now()}.json`, { type: 'application/json' });
      
      // Upload artifact
      const artifact = await uploadArtifact(projectId!, file, {
        name: `CSV Upload: ${metadata.template}`,
        description: `Bulk upload of ${data.length} opportunities from ${metadata.template}`,
        tags: ['csv_upload', metadata.source, 'bulk_import'],
      });
      
      console.log(`[CSV Upload] Artifact created: ${artifact.id}`);
      
      // Submit ingest job to parse and extract entities
      const job = await submitIngestJob(projectId!, artifact.id, {
        extract_entities: true,
        entity_types: ['govt_opportunity'], // Focus on opportunity entities
        skip_chunking: true, // Don't chunk JSON data
      });
      
      console.log(`[CSV Upload] Ingest job created: ${job.id}`);
      
      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['artifacts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['integration-sources', projectId] });
      
      // Show success message
      alert(`✅ CSV Upload Successful!\n\n` +
        `Uploaded: ${data.length} opportunities\n` +
        `Source: ${metadata.template}\n` +
        `Artifact ID: ${artifact.id}\n` +
        `Job ID: ${job.id}\n\n` +
        `The opportunities are now being processed and will appear in your opportunities list shortly.`);
    } catch (error: any) {
      console.error('[CSV Upload] Failed:', error);
      throw new Error(`CSV upload failed: ${error.message || 'Unknown error'}`);
    }
  };
  
  if (!projectId) {
    return (
      <EmptyState
        icon={<Database className="h-12 w-12" />}
        title="No Project Selected"
        description="Please select a project to view integrations"
      />
    );
  }
  
  if (loadingConfigs || loadingSources) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Loading integrations...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-lg">
            <Globe className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Data Source Integrations</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {filteredIntegrations.length} integration{filteredIntegrations.length !== 1 ? 's' : ''} in {ZONE_DEFINITIONS[selectedZone].name}
              {' · '}
              {activeCount} active, {unhealthyCount} unhealthy, {disabledCount} disabled
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setCSVUploadModalOpen(true)} 
            variant="primary"
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Upload className="h-4 w-4" />
            Upload CSV
          </Button>
          <Button 
            onClick={() => {
              setShouldCheckHealth(true);
              refetchHealth();
            }} 
            variant="default"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Health
          </Button>
        </div>
      </div>
      
      {/* Zone Selector */}
      <div className="flex gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
        {Object.values(ZONE_DEFINITIONS).map(zone => (
          <Button
            key={zone.id}
            variant={selectedZone === zone.id ? 'primary' : 'outline'}
            size="sm"
            onClick={() => {
              setSelectedZone(zone.id);
              zoneManager.switchZone(zone.id);
            }}
            className="flex items-center gap-2"
          >
            <span className="text-lg">{zone.icon}</span>
            <span>{zone.name}</span>
            <Badge variant="default" className="ml-1">
              {zone.dataSources.length}
            </Badge>
          </Button>
        ))}
      </div>
      
      {/* Zone Description */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 dark:text-blue-100">
              {ZONE_DEFINITIONS[selectedZone].icon} {ZONE_DEFINITIONS[selectedZone].name} Government Data
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {ZONE_DEFINITIONS[selectedZone].description}
            </p>
          </div>
        </div>
      </Card>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800 text-gray-900 dark:text-gray-100">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            <div>
              <div className="text-2xl font-bold text-green-900 dark:text-green-100">{activeCount}</div>
              <div className="text-sm text-green-700 dark:text-green-300">Active & Healthy</div>
            </div>
          </div>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border-red-200 dark:border-red-800 text-gray-900 dark:text-gray-100">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            <div>
              <div className="text-2xl font-bold text-red-900 dark:text-red-100">{unhealthyCount}</div>
              <div className="text-sm text-red-700 dark:text-red-300">Unhealthy</div>
            </div>
          </div>
        </Card>
        
        <Card className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
          <div className="flex items-center gap-3">
            <XCircle className="h-8 w-8 text-gray-600 dark:text-gray-400" />
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{disabledCount}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Disabled</div>
            </div>
          </div>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800 text-gray-900 dark:text-gray-100">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            <div>
              <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{integrations.length}</div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Total Sources</div>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search integrations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active & Healthy</option>
            <option value="unhealthy">Unhealthy</option>
            <option value="disabled">Disabled</option>
          </select>
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide' : 'Show'} Filters
              {(selectedCountries.length > 0 || selectedStates.length > 0) && (
                <Badge variant="default" className="ml-1">
                  {selectedCountries.length + selectedStates.length}
                </Badge>
              )}
            </Button>
          </div>
          
          {/* Advanced Filters */}
          {showFilters && (
            <div className="pt-4 border-t border-gray-200 dark:border-slate-700 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Country Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Country Filter
                    {selectedCountries.length > 0 && (
                      <Badge variant="default" className="text-xs">
                        {selectedCountries.length} selected
                      </Badge>
                    )}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {getAllCountries().map(country => (
                      <label
                        key={country}
                        className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCountries.includes(country)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCountries([...selectedCountries, country]);
                            } else {
                              setSelectedCountries(selectedCountries.filter(c => c !== country));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{country}</span>
                      </label>
                    ))}
                  </div>
                  {selectedCountries.length > 0 && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setSelectedCountries([])}
                      className="mt-2 text-xs"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
                
                {/* State/Local Filter (for future extensibility) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    State/Local Filter
                    {selectedStates.length > 0 && (
                      <Badge variant="default" className="text-xs">
                        {selectedStates.length} selected
                      </Badge>
                    )}
                  </label>
                  {getAllStates().length === 0 ? (
                    <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No state/local sources available yet.
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        This filter will activate when state/local data sources are added.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {getAllStates().map(state => (
                          <label
                            key={state}
                            className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedStates.includes(state)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedStates([...selectedStates, state]);
                                } else {
                                  setSelectedStates(selectedStates.filter(s => s !== state));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{state}</span>
                          </label>
                        ))}
                      </div>
                      {selectedStates.length > 0 && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => setSelectedStates([])}
                          className="mt-2 text-xs"
                        >
                          Clear All
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Active Filters Summary */}
              {(selectedCountries.length > 0 || selectedStates.length > 0) && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Filter className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-900 dark:text-blue-100 font-medium">
                    Active Filters:
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {selectedCountries.map(country => (
                      <Badge key={country} variant="default" className="text-xs flex items-center gap-1">
                        {country}
                        <button
                          onClick={() => setSelectedCountries(selectedCountries.filter(c => c !== country))}
                          className="hover:text-red-600"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                    {selectedStates.map(state => (
                      <Badge key={state} variant="default" className="text-xs flex items-center gap-1">
                        {state}
                        <button
                          onClick={() => setSelectedStates(selectedStates.filter(s => s !== state))}
                          className="hover:text-red-600"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      setSelectedCountries([]);
                      setSelectedStates([]);
                    }}
                    className="ml-auto text-xs"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
      
      {/* Geographic and Service Line Filters */}
      {(geographicConfig || serviceLines) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {geographicConfig && (
            <GeographicFilter
              config={geographicConfig}
              filter={geographicFilter}
              onChange={setGeographicFilter}
              onClear={() => setGeographicFilter({})}
            />
          )}
          {serviceLines && (
            <ServiceLineFilter
              serviceLines={serviceLines}
              selectedServiceLines={selectedServiceLines}
              onChange={setSelectedServiceLines}
              onClear={() => setSelectedServiceLines([])}
            />
          )}
        </div>
      )}
      
      {/* Integrations List - Grouped by Type */}
      <div className="space-y-8">
        {/* Opportunities & Tenders Section */}
        {(() => {
          const oppIntegrations = filteredIntegrations.filter(i => 
            ['canada_grants', 'canada_tenders', 'samgov', 'worldbank', 'bc_bid', 'bids_tender', 'bonfire', 'merx', 'sap_ariba', 'jaggaer', 'city_portals'].includes(i.source)
          );
          if (oppIntegrations.length === 0) return null;
          
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-2 border-b-2 border-blue-500">
                <Award className="h-7 w-7 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Opportunities & Tenders
              </h2>
                <Badge variant="default" className="ml-auto text-lg px-3 py-1">
                  {oppIntegrations.length} {oppIntegrations.length === 1 ? 'Source' : 'Sources'}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Government procurement opportunities, grants, and tenders from multiple jurisdictions
              </p>
              <div className="space-y-3">
                {oppIntegrations.map((integration) => {
          const entry = allSources.find(s => s.metadata.id === integration.source)!;
        const isExpanded = expandedSource === integration.source;
        const preview = previewData[integration.source];
        
        const statusIcon = integration.enabled
            ? integration.health?.healthy
              ? CheckCircle
              : integration.health?.status === 'degraded'
              ? AlertCircle
              : XCircle
            : Clock;
          
          const statusColor = integration.enabled
            ? integration.health?.healthy
              ? 'text-green-600'
              : integration.health?.status === 'degraded'
              ? 'text-yellow-600'
              : 'text-red-600'
            : 'text-gray-400';
          
        const StatusIcon = statusIcon;
        
        return (
            <Card key={integration.source} className="hover:shadow-lg transition-shadow">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getSourceIcon(integration.source)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{entry.metadata.name}</h3>
                          <Badge variant={integration.enabled ? 'default' : 'default'}>
                            {integration.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                          {entry.metadata.requires_api_key && (
                            <Badge variant="warning">
                              <Key className="h-3 w-3 mr-1" />
                              API Key Required
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{entry.metadata.description}</p>
                      </div>
                    </div>
                    
                    {/* Metadata - Dynamically Updated */}
                    {(() => {
                      const metrics = getMetrics(integration, sourceMetrics);
                      
                      // Map metrics to SyncStatus format
                      const mapStatusToSyncStatus = (status: string): 'success' | 'error' | 'in_progress' | 'pending' | 'never' => {
                        if (status === 'healthy') return 'success';
                        if (status === 'error' || status === 'unhealthy') return 'error';
                        if (status === 'Disabled') return 'never';
                        if (status === 'Unknown' || !status) return 'never';
                        return 'pending';
                      };
                      
                      // Extract response time in milliseconds
                      const extractResponseTime = (responseTime: string): number | undefined => {
                        const match = responseTime.match(/(\d+)ms/);
                        return match ? parseInt(match[1], 10) : undefined;
                      };
                      
                      // Parse lastSync to ISO string or null
                      const parseLastSync = (lastSync: string): string | null => {
                        if (lastSync === 'Never' || !lastSync) return null;
                        try {
                          const date = new Date(lastSync);
                          return isNaN(date.getTime()) ? null : date.toISOString();
                        } catch {
                          return null;
                        }
                      };
                      
                      const syncStatus: SyncStatus = {
                        status: mapStatusToSyncStatus(metrics.status),
                        lastSync: parseLastSync(metrics.lastSync),
                        itemCount: metrics.itemCount,
                        syncDuration: extractResponseTime(metrics.responseTime),
                      };
                      
                      return (
                        <div className="space-y-3">
                          {/* Sync Status Badge */}
                          <div className="flex items-center justify-between">
                            <SyncStatusBadge 
                              status={syncStatus} 
                              size="md" 
                              showDetails={true}
                            />
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Response: {metrics.responseTime}
                            </div>
                          </div>
                          
                          {/* Additional Metrics Grid */}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                              <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Last Sync</div>
                              <div className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                                {metrics.lastSync}
                              </div>
                            </div>
                            
                            <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                              <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Opportunities</div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {metrics.itemCount}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Health Message - Only show for actual errors, not unknown status */}
                    {integration.enabled && integration.health && !integration.health.healthy && integration.health.status === 'error' && (
                      <div className="mt-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-gray-900 dark:text-gray-100">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-red-900 dark:text-red-100">Health Check Failed</div>
                            <div className="text-sm text-red-700 dark:text-red-300">{integration.health.message}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Expanded Preview */}
                    {isExpanded && preview && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          Preview (First 5 Opportunities)
                        </h4>
                        {previewMutation.isPending ? (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <LoadingSpinner size="sm" />
                            Loading preview...
                          </div>
                        ) : preview.error ? (
                          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-gray-900 dark:text-gray-100">
                            <div className="text-sm font-medium text-red-900 dark:text-red-100">Error loading preview</div>
                            <div className="text-xs text-red-700 dark:text-red-300 mt-1">{preview.error}</div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {preview.opportunities && Array.isArray(preview.opportunities) && preview.opportunities.length > 0 ? (
                              preview.opportunities.map((opp: any, idx: number) => (
                                <div key={idx} className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow text-gray-900 dark:text-gray-100">
                                  {/* Title and Status */}
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-base flex-1">
                                      {opp.title || opp.name || 'Untitled Opportunity'}
                                    </div>
                                    {opp.status && (
                                      <Badge variant={opp.status === 'open' ? 'default' : 'default'}>
                                        {opp.status}
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* Organization */}
                                  <div className="text-sm text-gray-700 font-medium mb-2">
                                    {opp.organization || opp.agency || 'Unknown Organization'}
                                  </div>
                                  
                                  {/* Grid of key information */}
                                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                    {opp.location && (
                                      <div>
                                        <span className="text-gray-500">Location:</span>
                                        <span className="ml-1 text-gray-800 font-medium">
                                          {typeof opp.location === 'string' ? opp.location : (opp.location.country || opp.location.region || 'N/A')}
                                        </span>
                                      </div>
                                    )}
                                    {opp.value && (
                                      <div>
                                        <span className="text-gray-500">Value:</span>
                                        <span className="ml-1 text-gray-800 font-medium">{opp.value}</span>
                                      </div>
                                    )}
                                  {opp.deadline && (
                                      <div>
                                        <span className="text-gray-500">Deadline:</span>
                                        <span className="ml-1 text-gray-800 font-medium">
                                          {new Date(opp.deadline).toLocaleDateString()}
                                        </span>
                                    </div>
                                  )}
                                  {opp.postedDate && (
                                      <div>
                                        <span className="text-gray-500">Posted:</span>
                                        <span className="ml-1 text-gray-800 font-medium">
                                          {new Date(opp.postedDate).toLocaleDateString()}
                                        </span>
                                      </div>
                                    )}
                                    {opp.metadata?.referenceNumber && (
                                      <div>
                                        <span className="text-gray-500">Ref #:</span>
                                        <span className="ml-1 text-gray-800 font-medium font-mono text-[10px]">
                                          {opp.metadata.referenceNumber}
                                        </span>
                                      </div>
                                    )}
                                    {opp.metadata?.solicitationNumber && (
                                      <div>
                                        <span className="text-gray-500">Sol #:</span>
                                        <span className="ml-1 text-gray-800 font-medium font-mono text-[10px]">
                                          {opp.metadata.solicitationNumber}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Canada Tenders specific dates */}
                                  {opp.metadata?.dates && opp.source !== 'canada_grants' && (
                                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-2 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                      <div className="font-semibold mb-1">Contract Timeline:</div>
                                      <div className="grid grid-cols-2 gap-1">
                                        {opp.metadata.dates.expectedStart && (
                                          <div>Start: {new Date(opp.metadata.dates.expectedStart).toLocaleDateString()}</div>
                                        )}
                                        {opp.metadata.dates.expectedEnd && (
                                          <div>End: {new Date(opp.metadata.dates.expectedEnd).toLocaleDateString()}</div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Canada Grants specific info */}
                                  {opp.source === 'canada_grants' && opp.metadata && (
                                    <div className="space-y-2">
                                      {/* Grant Details */}
                                      <div className="text-xs text-gray-600 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 p-2 rounded">
                                        <div className="font-semibold mb-1">Grant Details:</div>
                                        <div className="grid grid-cols-2 gap-1">
                                          <div>Type: {opp.metadata.agreementType || 'N/A'}</div>
                                          {opp.metadata.programName && (
                                            <div className="col-span-2">Program: {opp.metadata.programName}</div>
                                          )}
                                          {opp.metadata.dates?.start && (
                                            <div>Start: {new Date(opp.metadata.dates.start).toLocaleDateString()}</div>
                                          )}
                                          {opp.metadata.dates?.end && (
                                            <div>End: {new Date(opp.metadata.dates.end).toLocaleDateString()}</div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Recipient Info */}
                                      {opp.metadata.recipient && (
                                        <div className="text-xs text-gray-600 dark:text-gray-300 bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded">
                                          <div className="font-semibold mb-1">Recipient:</div>
                                          <div>{opp.metadata.recipient}</div>
                                          {opp.metadata.recipientType && (
                                            <div className="text-[10px] text-gray-500 mt-0.5">Type: {opp.metadata.recipientType}</div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Program Purpose */}
                                      {opp.metadata.programPurpose && (
                                        <div className="text-xs text-gray-600 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                                          <div className="font-semibold mb-1">Purpose:</div>
                                          <div className="line-clamp-2">{opp.metadata.programPurpose}</div>
                                        </div>
                                      )}
                                      
                                      {/* Expected Results */}
                                      {opp.metadata.expectedResults && (
                                        <div className="text-xs text-gray-600 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 p-2 rounded">
                                          <div className="font-semibold mb-1">Expected Results:</div>
                                          <div className="line-clamp-2">{opp.metadata.expectedResults}</div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* World Bank specific financial data */}
                                  {opp.metadata?.totalAmount && opp.metadata.totalAmount !== '0' && (
                                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-2 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                                      <div className="font-semibold mb-1">Financial Details:</div>
                                      <div className="grid grid-cols-2 gap-1">
                                        <div>Total: ${parseFloat(opp.metadata.totalAmount.replace(/,/g, '')).toLocaleString()}</div>
                                        {opp.metadata.ibrdCommitment && opp.metadata.ibrdCommitment !== '0' && (
                                          <div>IBRD: ${parseFloat(opp.metadata.ibrdCommitment.replace(/,/g, '')).toLocaleString()}</div>
                                        )}
                                        {opp.metadata.idaCommitment && opp.metadata.idaCommitment !== '0' && (
                                          <div>IDA: ${parseFloat(opp.metadata.idaCommitment.replace(/,/g, '')).toLocaleString()}</div>
                                        )}
                                        {opp.metadata.projectCost && (
                                          <div>Cost: ${parseFloat(opp.metadata.projectCost.replace(/,/g, '')).toLocaleString()}</div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Contact info for Canada Tenders */}
                                  {opp.metadata?.contact && (opp.metadata.contact.name || opp.metadata.contact.email) && (
                                    <div className="text-xs text-gray-600 bg-green-50 p-2 rounded">
                                      <div className="font-semibold mb-1">Contact:</div>
                                      {opp.metadata.contact.name && <div>{opp.metadata.contact.name}</div>}
                                      {opp.metadata.contact.email && (
                                        <div>
                                          <a href={`mailto:${opp.metadata.contact.email}`} className="text-blue-600 hover:underline">
                                            {opp.metadata.contact.email}
                                          </a>
                                        </div>
                                      )}
                                      {opp.metadata.contact.phone && <div>{opp.metadata.contact.phone}</div>}
                                    </div>
                                  )}
                                  
                                  {/* Link */}
                                  {opp.url && (
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                      <a
                                        href={opp.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                      >
                                        View Details →
                                      </a>
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-gray-500 italic">
                                No opportunities found in preview. Try syncing to fetch data.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 ml-4">
                    {requiresCredentials(integration.source) && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => handleOpenCredentialModal(integration)}
                        title="Manage credentials"
                        className={integration.config.api_key ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-gray-900 dark:text-gray-100' : 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 text-gray-900 dark:text-gray-100'}
                      >
                        <Key className={`h-4 w-4 ${integration.config.api_key ? 'text-green-600' : 'text-yellow-600'}`} />
                        Credentials
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => {
                        if (isExpanded) {
                          setExpandedSource(null);
                        } else {
                          previewMutation.mutate({
                            source: integration.source,
                            config: integration.config,
                          });
                        }
                      }}
                      disabled={!integration.enabled || previewMutation.isPending}
                      title="Preview sample opportunities"
                    >
                      <Eye className="h-4 w-4" />
                      {isExpanded ? 'Collapse' : 'Preview'}
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleOpenSyncModal(integration)}
                      disabled={!integration.enabled || syncMutation.isPending}
                      title="Sync all opportunities"
                    >
                      <Play className="h-4 w-4" />
                      Sync Now
                    </Button>
                  </div>
                </div>
                
                {/* API Info */}
                <div className="text-xs text-gray-500">
                  <a
                    href={entry.metadata.documentation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    View API Documentation →
                  </a>
                  {' · '}
                  Rate Limits: {entry.metadata.rate_limits.public || entry.metadata.rate_limits.authenticated}
                </div>
              </div>
            </Card>
                );
              })}
              </div>
            </div>
          );
        })()}

        {/* Awards & Spending Section */}
        {(() => {
          const spendingIntegrations = filteredIntegrations.filter(i => 
            ['usa_awards', 'usa_budgets'].includes(i.source)
          );
          if (spendingIntegrations.length === 0) return null;
          
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-2 border-b-2 border-green-500">
                <DollarSign className="h-7 w-7 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Awards & Spending Data
              </h2>
                <Badge variant="default" className="ml-auto text-lg px-3 py-1">
                  {spendingIntegrations.length} {spendingIntegrations.length === 1 ? 'Source' : 'Sources'}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Federal spending analysis, contract awards, and budget tracking
              </p>
              <div className="space-y-3">
                {spendingIntegrations.map((integration) => {
                  const entry = allSources.find(s => s.metadata.id === integration.source)!;
                  const isExpanded = expandedSource === integration.source;
                  const preview = previewData[integration.source];
                  const statusIcon = integration.enabled
                    ? integration.health?.healthy ? CheckCircle
                    : integration.health?.status === 'degraded' ? AlertCircle
                    : XCircle : Clock;
                  const statusColor = integration.enabled
                    ? integration.health?.healthy ? 'text-green-600'
                    : integration.health?.status === 'degraded' ? 'text-yellow-600'
                    : 'text-red-600' : 'text-gray-400';
                  const StatusIcon = statusIcon;
                  
                  return (
                    <Card key={integration.source} className="hover:shadow-lg transition-shadow">
                      {/* Same card content as above */}
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getSourceIcon(integration.source)}
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{entry.metadata.name}</h3>
                                  <Badge variant={integration.enabled ? 'default' : 'default'}>
                                    {integration.enabled ? 'Enabled' : 'Disabled'}
                                  </Badge>
                                  {entry.metadata.requires_api_key && (
                                    <Badge variant="warning">
                                      <Key className="h-3 w-3 mr-1" />
                                      API Key Required
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{entry.metadata.description}</p>
                              </div>
                            </div>
                            
                            {/* Metadata - Dynamically Updated */}
                            {(() => {
                              const metrics = getMetrics(integration, sourceMetrics);
                              
                              // Map metrics to SyncStatus format
                              const mapStatusToSyncStatus = (status: string): 'success' | 'error' | 'in_progress' | 'pending' | 'never' => {
                                if (status === 'healthy') return 'success';
                                if (status === 'error' || status === 'unhealthy') return 'error';
                                if (status === 'Disabled') return 'never';
                                if (status === 'Unknown' || !status) return 'never';
                                return 'pending';
                              };
                              
                              // Extract response time in milliseconds
                              const extractResponseTime = (responseTime: string): number | undefined => {
                                const match = responseTime.match(/(\d+)ms/);
                                return match ? parseInt(match[1], 10) : undefined;
                              };
                              
                              // Parse lastSync to ISO string or null
                              const parseLastSync = (lastSync: string): string | null => {
                                if (lastSync === 'Never' || !lastSync) return null;
                                try {
                                  const date = new Date(lastSync);
                                  return isNaN(date.getTime()) ? null : date.toISOString();
                                } catch {
                                  return null;
                                }
                              };
                              
                              const syncStatus: SyncStatus = {
                                status: mapStatusToSyncStatus(metrics.status),
                                lastSync: parseLastSync(metrics.lastSync),
                                itemCount: metrics.itemCount,
                                syncDuration: extractResponseTime(metrics.responseTime),
                              };
                              
                              return (
                                <div className="space-y-3">
                                  {/* Sync Status Badge */}
                                  <div className="flex items-center justify-between">
                                    <SyncStatusBadge 
                                      status={syncStatus} 
                                      size="md" 
                                      showDetails={true}
                                    />
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      Response: {metrics.responseTime}
                                    </div>
                                  </div>
                                  
                                  {/* Additional Metrics Grid */}
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Last Sync</div>
                                      <div className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                                        {metrics.lastSync}
                                      </div>
                                    </div>
                                    
                                    <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Awards</div>
                                      <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {metrics.itemCount}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                            
                            {integration.enabled && integration.health && !integration.health.healthy && integration.health.status === 'error' && (
                              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium text-red-900">Health Check Failed</div>
                                    <div className="text-sm text-red-700">{integration.health.message}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {isExpanded && preview && (
                              <div className="mt-4 pt-4 border-t">
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Preview (First 5 Opportunities)</h4>
                                {previewMutation.isPending ? (
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <LoadingSpinner size="sm" />
                                    Loading preview...
                                  </div>
                                ) : preview.error ? (
                                  <div className="bg-red-50 border border-red-200 rounded p-3">
                                    <div className="text-sm font-medium text-red-900">Error loading preview</div>
                                    <div className="text-xs text-red-700 mt-1">{preview.error}</div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {preview.opportunities && Array.isArray(preview.opportunities) && preview.opportunities.length > 0 ? (
                                      preview.opportunities.map((opp: any, idx: number) => (
                                        <div key={idx} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3 text-gray-900 dark:text-gray-100">
                                          <div className="font-medium text-blue-900 dark:text-blue-100">{opp.title || opp.name || 'Untitled Opportunity'}</div>
                                          <div className="text-sm text-blue-700 dark:text-blue-300 mt-1">{opp.organization || opp.agency || 'Unknown'}</div>
                                          {opp.deadline && (
                                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Deadline: {new Date(opp.deadline).toLocaleDateString()}</div>
                                          )}
                                          {opp.postedDate && (
                                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Posted: {new Date(opp.postedDate).toLocaleDateString()}</div>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-sm text-gray-500 italic">No opportunities found in preview. Try syncing to fetch data.</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => {
                                if (isExpanded) {
                                  setExpandedSource(null);
                                } else {
                                  previewMutation.mutate({ source: integration.source, config: integration.config });
                                }
                              }}
                              disabled={!integration.enabled || previewMutation.isPending}
                              title="Preview sample opportunities"
                            >
                              <Eye className="h-4 w-4" />
                              {isExpanded ? 'Collapse' : 'Preview'}
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="default"
                            onClick={() => handleOpenSyncModal(integration)}
                              disabled={!integration.enabled || syncMutation.isPending}
                              title="Sync all opportunities"
                            >
                              <Play className="h-4 w-4" />
                              Sync Now
                            </Button>
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          <a href={entry.metadata.documentation_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            View API Documentation →
                          </a>
                          {' · '}
                          Rate Limits: {entry.metadata.rate_limits.public || entry.metadata.rate_limits.authenticated}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* RSS Feeds & News Section */}
        {(() => {
          const rssIntegrations = filteredIntegrations.filter(i => i.source === 'rss_feeds');
          if (rssIntegrations.length === 0) return null;
          
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-2 border-b-2 border-orange-500">
                <Rss className="h-7 w-7 text-orange-600" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  RSS Feeds & News
                </h2>
                <Badge variant="default" className="ml-auto text-lg px-3 py-1">
                  {RSS_FEED_REGISTRY.length} Feeds
                </Badge>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Real-time news and updates from {RSS_FEED_REGISTRY.length} curated government and industry RSS feeds
              </p>
              
              {rssIntegrations.map((integration) => {
                const entry = allSources.find(s => s.metadata.id === integration.source)!;
                const isExpanded = expandedSource === integration.source;
                const preview = previewData[integration.source];
                const statusIcon = integration.enabled
                  ? integration.health?.healthy ? CheckCircle
                  : integration.health?.status === 'degraded' ? AlertCircle
                  : XCircle : Clock;
                const statusColor = integration.enabled
                  ? integration.health?.healthy ? 'text-green-600'
                  : integration.health?.status === 'degraded' ? 'text-yellow-600'
                  : 'text-red-600' : 'text-gray-400';
                const StatusIcon = statusIcon;
                
                return (
                  <Card key={integration.source} className="hover:shadow-lg transition-shadow">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Rss className="h-6 w-6 text-orange-600" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{entry.metadata.name}</h3>
                                <Badge variant={integration.enabled ? 'default' : 'default'}>
                                  {integration.enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{entry.metadata.description}</p>
                            </div>
                          </div>
                          
                          {/* RSS Feed Details Collapsible */}
                          <div className="mt-3">
                            <button
                              onClick={() => setShowRSSFeedDetails(!showRSSFeedDetails)}
                              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              {showRSSFeedDetails ? (
                                <>
                                  <ChevronDown className="h-4 w-4" />
                                  Hide Feed Details ({RSS_FEED_REGISTRY.length} feeds)
                                </>
                              ) : (
                                <>
                                  <ChevronRight className="h-4 w-4" />
                                  Show Feed Details ({RSS_FEED_REGISTRY.length} feeds)
                                </>
                              )}
                            </button>
                            
                            {showRSSFeedDetails && (
                              <div className="mt-3 p-4 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                                  <Radio className="h-4 w-4" />
                                  Active RSS Feed Sources
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {RSS_FEED_REGISTRY.map((feed) => (
                                    <div key={feed.id} className="p-3 bg-white dark:bg-slate-900 rounded border border-gray-200 dark:border-slate-700">
                                      <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                                            {feed.name}
                                          </div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {CATEGORY_NAMES[feed.category]}
                                          </div>
                                        </div>
                                        <div className="flex gap-1">
                                          <Badge 
                                            variant={feed.priority === 1 ? 'default' : 'default'}
                                            className="text-xs whitespace-nowrap"
                                          >
                                            P{feed.priority}
                                          </Badge>
                                          {feed.verified && (
                                            <Badge variant="success" className="text-xs">
                                              ✓
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                                        {feed.description}
                                      </p>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant="default"
                                          className="text-xs h-7 px-2"
                                          onClick={async () => {
                                            setRssTestResults(prev => ({ ...prev, [feed.id]: { status: 'testing' } }));
                                            try {
                                              const response = await fetch(feed.url, { 
                                                method: 'HEAD',
                                                mode: 'no-cors'
                                              });
                                              setRssTestResults(prev => ({ 
                                                ...prev, 
                                                [feed.id]: { 
                                                  status: 'success',
                                                  message: 'Feed accessible'
                                                } 
                                              }));
                                            } catch (error: any) {
                                              setRssTestResults(prev => ({ 
                                                ...prev, 
                                                [feed.id]: { 
                                                  status: 'error',
                                                  message: error.message || 'Connection failed'
                                                } 
                                              }));
                                            }
                                          }}
                                        >
                                          {rssTestResults[feed.id]?.status === 'testing' ? (
                                            <>
                                              <LoadingSpinner size="sm" className="mr-1" />
                                              Testing...
                                            </>
                                          ) : rssTestResults[feed.id]?.status === 'success' ? (
                                            <>
                                              <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                                              OK
                                            </>
                                          ) : rssTestResults[feed.id]?.status === 'error' ? (
                                            <>
                                              <XCircle className="h-3 w-3 mr-1 text-red-600" />
                                              Failed
                                            </>
                                          ) : (
                                            <>
                                              <Zap className="h-3 w-3 mr-1" />
                                              Test Feed
                                            </>
                                          )}
                                        </Button>
                                        <a
                                          href={feed.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-blue-600 hover:underline truncate flex-1"
                                        >
                                          View Feed →
                                        </a>
                                      </div>
                                      {rssTestResults[feed.id]?.message && (
                                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                          {rssTestResults[feed.id].message}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                
                                {/* RSS Feed Statistics */}
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 grid grid-cols-3 gap-4">
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-orange-600">
                                      {RSS_FEED_REGISTRY.filter(f => f.priority === 1).length}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">High Priority</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-green-600">
                                      {RSS_FEED_REGISTRY.filter(f => f.verified).length}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">Verified</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-600">
                                      {RSS_FEED_REGISTRY.filter(f => f.enabled).length}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">Active</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Metadata - Dynamically Updated */}
                          {(() => {
                            const metrics = getMetrics(integration, sourceMetrics);
                            return (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4">
                                <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                  <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Status</div>
                                  <div className="flex items-center gap-1">
                                    <StatusIcon className={`h-4 w-4 ${metrics.statusColor}`} />
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {metrics.status}
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                  <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Response Time</div>
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {metrics.responseTime}
                                  </div>
                                </div>
                                
                                <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                  <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Last Sync</div>
                                  <div className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                                    {metrics.lastSync}
                                  </div>
                                </div>
                                
                                <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                  <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">RSS Items</div>
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {metrics.itemCount}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          
                          {integration.enabled && integration.health && !integration.health.healthy && (
                            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                                <div>
                                  <div className="text-sm font-medium text-red-900">Health Check Failed</div>
                                  <div className="text-sm text-red-700">{integration.health.message}</div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {isExpanded && preview && (
                            <div className="mt-4 pt-4 border-t">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Preview (First 5 RSS Items)</h4>
                              {previewMutation.isPending ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <LoadingSpinner size="sm" />
                                  Loading preview...
                                </div>
                              ) : preview.error ? (
                                <div className="bg-red-50 border border-red-200 rounded p-3">
                                  <div className="text-sm font-medium text-red-900">Error loading preview</div>
                                  <div className="text-xs text-red-700 mt-1">{preview.error}</div>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {preview.opportunities && Array.isArray(preview.opportunities) && preview.opportunities.length > 0 ? (
                                    preview.opportunities.map((opp: any, idx: number) => (
                                      <div key={idx} className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded p-3 text-gray-900 dark:text-gray-100">
                                        <div className="font-medium text-orange-900 dark:text-orange-100">{opp.title || 'Untitled'}</div>
                                        <div className="text-sm text-orange-700 dark:text-orange-300 mt-1">{opp.organization || opp.source || 'Unknown Source'}</div>
                                        {opp.description && (
                                          <div className="text-xs text-orange-600 dark:text-orange-400 mt-1 line-clamp-2">{opp.description}</div>
                                        )}
                                        {opp.deadline && (
                                          <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">Published: {new Date(opp.deadline).toLocaleDateString()}</div>
                                        )}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="text-sm text-gray-500 italic">No RSS items found in preview. Try syncing to fetch data.</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedSource(null);
                              } else {
                                previewMutation.mutate({ source: integration.source, config: integration.config });
                              }
                            }}
                            disabled={!integration.enabled || previewMutation.isPending}
                            title="Preview sample RSS items"
                          >
                            <Eye className="h-4 w-4" />
                            {isExpanded ? 'Collapse' : 'Preview'}
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="default"
                          onClick={() => handleOpenSyncModal(integration)}
                          disabled={!integration.enabled || syncMutation.isPending}
                          title="Sync all RSS items"
                        >
                          <Play className="h-4 w-4" />
                          Sync Now
                        </Button>
                        </div>
                      </div>
                      
                      <div className="text-xs text-gray-500">
                        <a href={entry.metadata.documentation_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          Learn about RSS Feeds →
                        </a>
                        {' · '}
                        Rate Limits: {entry.metadata.rate_limits.public}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          );
        })()}

        {/* Policy & Regulatory Section */}
        {(() => {
          const policyIntegrations = filteredIntegrations.filter(i => 
            ['federal_register', 'executive_orders'].includes(i.source)
          );
          if (policyIntegrations.length === 0) return null;
          
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-2 border-b-2 border-purple-500">
                <Scale className="h-7 w-7 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Policy & Regulatory
                </h2>
                <Badge variant="default" className="ml-auto text-lg px-3 py-1">
                  {policyIntegrations.length} {policyIntegrations.length === 1 ? 'Source' : 'Sources'}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Federal regulations, executive orders, and policy documents
              </p>
        <div className="space-y-3">
                {policyIntegrations.map((integration) => {
                  const entry = allSources.find(s => s.metadata.id === integration.source)!;
                  const isExpanded = expandedSource === integration.source;
                  const preview = previewData[integration.source];
                  const statusIcon = integration.enabled
                    ? integration.health?.healthy ? CheckCircle
                    : integration.health?.status === 'degraded' ? AlertCircle
                    : XCircle : Clock;
                  const statusColor = integration.enabled
                    ? integration.health?.healthy ? 'text-green-600'
                    : integration.health?.status === 'degraded' ? 'text-yellow-600'
                    : 'text-red-600' : 'text-gray-400';
                  const StatusIcon = statusIcon;
                  
                  return (
                    <Card key={integration.source} className="hover:shadow-lg transition-shadow">
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getSourceIcon(integration.source)}
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{entry.metadata.name}</h3>
                                  <Badge variant={integration.enabled ? 'default' : 'default'}>
                                    {integration.enabled ? 'Enabled' : 'Disabled'}
                                  </Badge>
                                  {entry.metadata.requires_api_key && (
                                    <Badge variant="warning">
                                      <Key className="h-3 w-3 mr-1" />
                                      API Key Required
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{entry.metadata.description}</p>
                              </div>
                            </div>
                            
                            {/* Metadata - Dynamically Updated */}
                            {(() => {
                              const metrics = getMetrics(integration, sourceMetrics);
                              return (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Status</div>
                                    <div className="flex items-center gap-1">
                                      <StatusIcon className={`h-4 w-4 ${metrics.statusColor}`} />
                                      <span className="font-medium text-gray-900 dark:text-gray-100">
                                        {metrics.status}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Response Time</div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                      {metrics.responseTime}
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Last Sync</div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                                      {metrics.lastSync}
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Documents</div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                      {metrics.itemCount}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                            
                            {integration.enabled && integration.health && !integration.health.healthy && integration.health.status === 'error' && (
                              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium text-red-900">Health Check Failed</div>
                                    <div className="text-sm text-red-700">{integration.health.message}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {isExpanded && preview && (
                              <div className="mt-4 pt-4 border-t">
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Preview (First 5 Documents)</h4>
                                {previewMutation.isPending ? (
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <LoadingSpinner size="sm" />
                                    Loading preview...
                                  </div>
                                ) : preview.error ? (
                                  <div className="bg-red-50 border border-red-200 rounded p-3">
                                    <div className="text-sm font-medium text-red-900">Error loading preview</div>
                                    <div className="text-xs text-red-700 mt-1">{preview.error}</div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {preview.opportunities && Array.isArray(preview.opportunities) && preview.opportunities.length > 0 ? (
                                      preview.opportunities.map((opp: any, idx: number) => (
                                        <div key={idx} className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded p-3 text-gray-900 dark:text-gray-100">
                                          <div className="font-medium text-purple-900 dark:text-purple-100">{opp.title || opp.name || 'Untitled Document'}</div>
                                          <div className="text-sm text-purple-700 dark:text-purple-300 mt-1">{opp.organization || opp.agency || 'Unknown'}</div>
                                          {opp.description && (
                                            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1 line-clamp-2">{opp.description}</div>
                                          )}
                                          {opp.deadline && (
                                            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">Published: {new Date(opp.deadline).toLocaleDateString()}</div>
                                          )}
                                          {opp.postedDate && (
                                            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">Posted: {new Date(opp.postedDate).toLocaleDateString()}</div>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-sm text-gray-500 italic">No documents found in preview. Try syncing to fetch data.</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => {
                                if (isExpanded) {
                                  setExpandedSource(null);
                                } else {
                                  previewMutation.mutate({ source: integration.source, config: integration.config });
                                }
                              }}
                              disabled={!integration.enabled || previewMutation.isPending}
                              title="Preview sample documents"
                            >
                              <Eye className="h-4 w-4" />
                              {isExpanded ? 'Collapse' : 'Preview'}
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="default"
                            onClick={() => handleOpenSyncModal(integration)}
                            disabled={!integration.enabled || syncMutation.isPending}
                            title="Sync all documents"
                          >
                            <Play className="h-4 w-4" />
                            Sync Now
                          </Button>
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          <a href={entry.metadata.documentation_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            View API Documentation →
                          </a>
                          {' · '}
                          Rate Limits: {entry.metadata.rate_limits.public || entry.metadata.rate_limits.authenticated}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* All Other Integrations (if any remain ungrouped) */}
        {(() => {
          const ungroupedIntegrations = filteredIntegrations.filter(i => 
            !['canada_grants', 'canada_tenders', 'samgov', 'worldbank', 'usa_awards', 'usa_budgets', 'rss_feeds', 'federal_register', 'executive_orders'].includes(i.source)
          );
          if (ungroupedIntegrations.length === 0) return null;
          
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-2 border-b-2 border-gray-400">
                <Database className="h-7 w-7 text-gray-600" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Other Integrations
                </h2>
                <Badge variant="default" className="ml-auto text-lg px-3 py-1">
                  {ungroupedIntegrations.length} {ungroupedIntegrations.length === 1 ? 'Source' : 'Sources'}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Additional data sources and integrations
              </p>
              <div className="space-y-3">
                {ungroupedIntegrations
            .map((integration) => {
              const entry = allSources.find(s => s.metadata.id === integration.source)!;
              const isExpanded = expandedSource === integration.source;
              const preview = previewData[integration.source];
              
              const statusIcon = integration.enabled
                ? integration.health?.healthy
                  ? CheckCircle
                  : integration.health?.status === 'degraded'
                  ? AlertCircle
                  : XCircle
                : Clock;
              
              const statusColor = integration.enabled
                ? integration.health?.healthy
                  ? 'text-green-600'
                  : integration.health?.status === 'degraded'
                  ? 'text-yellow-600'
                  : 'text-red-600'
                : 'text-gray-400';
              
              const StatusIcon = statusIcon;
              
              return (
                <Card key={integration.source} className="hover:shadow-lg transition-shadow">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getSourceIcon(integration.source)}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{entry.metadata.name}</h3>
                              <Badge variant={integration.enabled ? 'default' : 'default'}>
                                {integration.enabled ? 'Enabled' : 'Disabled'}
                              </Badge>
                              {entry.metadata.requires_api_key && (
                                <Badge variant="warning">
                                  <Key className="h-3 w-3 mr-1" />
                                  API Key Required
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{entry.metadata.description}</p>
                          </div>
                        </div>
                        
                            {/* Metadata - Dynamically Updated */}
                            {(() => {
                              const metrics = getMetrics(integration, sourceMetrics);
                              return (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Status</div>
                            <div className="flex items-center gap-1">
                                      <StatusIcon className={`h-4 w-4 ${metrics.statusColor}`} />
                                      <span className="font-medium text-gray-900 dark:text-gray-100">
                                        {metrics.status}
                              </span>
                            </div>
                          </div>
                          
                                  <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Response Time</div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                      {metrics.responseTime}
                            </div>
                          </div>
                          
                                  <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Last Sync</div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                                      {metrics.lastSync}
                            </div>
                          </div>
                          
                                  <div className="bg-gray-50 dark:bg-slate-800 px-3 py-2 rounded">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Items</div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                      {metrics.itemCount}
                            </div>
                          </div>
                        </div>
                              );
                            })()}
                        
                        {/* Health Message */}
                        {integration.enabled && integration.health && !integration.health.healthy && (
                          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-red-900">Health Check Failed</div>
                                <div className="text-sm text-red-700">{integration.health.message}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Expanded Preview */}
                        {isExpanded && preview && (
                          <div className="mt-4 pt-4 border-t">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                              Preview (First 5 Opportunities)
                            </h4>
                            {previewMutation.isPending ? (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <LoadingSpinner size="sm" />
                                Loading preview...
                              </div>
                            ) : preview.error ? (
                              <div className="bg-red-50 border border-red-200 rounded p-3">
                                <div className="text-sm font-medium text-red-900">Error loading preview</div>
                                <div className="text-xs text-red-700 mt-1">{preview.error}</div>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {preview.opportunities && Array.isArray(preview.opportunities) && preview.opportunities.length > 0 ? (
                                  preview.opportunities.map((opp: any, idx: number) => (
                                    <div key={idx} className="bg-blue-50 border border-blue-200 rounded p-3">
                                      <div className="font-medium text-blue-900">{opp.title || opp.name || 'Untitled Opportunity'}</div>
                                      <div className="text-sm text-blue-700 mt-1">{opp.organization || opp.agency || 'Unknown'}</div>
                                      {opp.deadline && (
                                        <div className="text-xs text-blue-600 mt-1">
                                          Deadline: {new Date(opp.deadline).toLocaleDateString()}
                                        </div>
                                      )}
                                      {opp.postedDate && (
                                        <div className="text-xs text-blue-600 mt-1">
                                          Posted: {new Date(opp.postedDate).toLocaleDateString()}
                                        </div>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-sm text-gray-500 italic">
                                    No opportunities found in preview. Try syncing to fetch data.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => {
                            if (isExpanded) {
                              setExpandedSource(null);
                            } else {
                              previewMutation.mutate({
                                source: integration.source,
                                config: integration.config,
                              });
                            }
                          }}
                          disabled={!integration.enabled || previewMutation.isPending}
                          title="Preview sample opportunities"
                        >
                          <Eye className="h-4 w-4" />
                          {isExpanded ? 'Collapse' : 'Preview'}
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() =>
                            syncMutation.mutate({
                              source: integration.source,
                              config: integration.config,
                            })
                          }
                          disabled={!integration.enabled || syncMutation.isPending}
                          title="Sync all opportunities"
                        >
                          <Play className="h-4 w-4" />
                          Sync Now
                        </Button>
                      </div>
                    </div>
                    
                    {/* API Info */}
                    <div className="text-xs text-gray-500">
                      <a
                        href={entry.metadata.documentation_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        View API Documentation →
                      </a>
                      {' · '}
                      Rate Limits: {entry.metadata.rate_limits.public || entry.metadata.rate_limits.authenticated}
                    </div>
                  </div>
                </Card>
              );
            })}
        </div>
            </div>
          );
        })()}
      </div>
      
      {filteredIntegrations.length === 0 && (
        <EmptyState
          icon={<Database className="h-12 w-12" />}
          title="No Integrations Found"
          description="Try adjusting your search or filter criteria"
        />
      )}
      
      {/* Sync Settings Modal */}
      {syncModalSource && (
        <SyncSettingsModal
          isOpen={syncModalOpen}
          onClose={() => {
            setSyncModalOpen(false);
            setSyncModalSource(null);
          }}
          onConfirm={handleConfirmSync}
          sourceId={syncModalSource.sourceId}
          sourceName={syncModalSource.sourceName}
          integrationType={syncModalSource.integrationType}
          initialFilters={{}}
        />
      )}
      
      {/* Credential Management Modal */}
      {credentialModalSource && (
        <CredentialManagementModal
          isOpen={credentialModalOpen}
          onClose={() => {
            setCredentialModalOpen(false);
            setCredentialModalSource(null);
          }}
          onSave={handleSaveCredentials}
          source={credentialModalSource.source}
          sourceName={credentialModalSource.sourceName}
          credentialFields={getCredentialFields(credentialModalSource.source)}
          existingCredentials={getExistingCredentials()}
        />
      )}
      
      {/* CSV Upload Modal */}
      <CSVUploadModal
        isOpen={csvUploadModalOpen}
        onClose={() => setCSVUploadModalOpen(false)}
        onUpload={handleCSVUpload}
        projectId={projectId!}
      />
    </div>
  );
}

// Helper function to get icon for each data source
function getSourceIcon(source: DataSourceType): React.ReactNode {
  const iconProps = { className: 'h-6 w-6' };
  
  switch (source) {
    case 'canada_grants':
    case 'canada_tenders':
      return <MapPin {...iconProps} className="h-6 w-6 text-red-600" />;
    case 'samgov':
    case 'usa_awards':
      return <Award {...iconProps} className="h-6 w-6 text-blue-600" />;
    case 'usa_budgets':
      return <DollarSign {...iconProps} className="h-6 w-6 text-green-600" />;
    case 'federal_register':
      return <FileText {...iconProps} className="h-6 w-6 text-purple-600" />;
    case 'executive_orders':
      return <Scale {...iconProps} className="h-6 w-6 text-indigo-600" />;
    case 'rss_feeds':
      return <Newspaper {...iconProps} className="h-6 w-6 text-orange-600" />;
    case 'worldbank':
      return <Globe {...iconProps} className="h-6 w-6 text-teal-600" />;
    case 'bc_bid':
    case 'bids_tender':
    case 'merx':
      return <MapPin {...iconProps} className="h-6 w-6 text-red-600" />; // Canadian sources
    case 'bonfire':
    case 'sap_ariba':
    case 'jaggaer':
      return <Award {...iconProps} className="h-6 w-6 text-blue-600" />; // Procurement platforms
    case 'city_portals':
      return <Building2 {...iconProps} className="h-6 w-6 text-cyan-600" />; // City/municipal
    default:
      return <Database {...iconProps} className="h-6 w-6 text-gray-600" />;
  }
}

